import React, { useEffect, useRef, useState } from "react";
import { useT, useLanguage } from "../../lib/LanguageContext";
import Pager from "../../components/ui/Pager";
import LoadFailure from "../../components/ui/LoadFailure";
import { Loader2, Percent, RefreshCcw, Wallet } from "lucide-react";
import { NoAccessPanel, isForbidden } from "../NoAccess";
import { useSecondaryRead } from "../screenAccess";
import { usePagedList } from "../usePagedList";
import { Money } from "../globalDataCells";
import { financeCodeText } from "./financeErrorText";
import { moneyText } from "./customPriceText";
import { instantDay } from "../../lib/calendarDay";

export const HISTORY_PAGE_SIZE = 10;

const STATUS_TONES = {
  issued: "bg-emerald-100 text-emerald-700",
  succeeded: "bg-emerald-100 text-emerald-700",
  error: "bg-rose-100 text-rose-700",
  unverified: "bg-amber-100 text-amber-700",
  pending: "bg-amber-100 text-amber-700",
  issued_demo: "bg-slate-100 text-slate-600",
  succeeded_demo: "bg-slate-100 text-slate-600",
};

// What a failed row says when the server stored no `error_code` for it. A legacy `error` row
// predates codes: the sentence says only that nothing was applied. An unconfirmed row always
// has a precise sentence for its kind.
const UNCODED_FAILURE = {
  credit: { error: "t360f.history.notApplied", unverified: "t360f.error.REBATE_UNVERIFIED" },
  refund: { error: "t360f.history.notApplied", unverified: "t360f.error.REFUND_UNVERIFIED" },
};

function HistoryRow({ kind, id, icon, amount, reason, by, at, status, errorCode }) {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const Icon = icon;
  const failureKey = UNCODED_FAILURE[kind][status];
  const failure = errorCode ? financeCodeText(errorCode, t) : failureKey ? t(failureKey) : null;
  return (
    <div className="flex items-start gap-3 text-sm bg-white rounded-lg p-3" data-testid={`${kind}-${id}`}>
      <Icon className={`w-4 h-4 mt-0.5 ${kind === "credit" ? "text-amber-600" : "text-rose-600"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Money value={amount} />
          <span className="text-slate-500 break-words">· {reason}</span>
          <span className={`text-xs rounded-full px-2 py-0.5 ${STATUS_TONES[status] || "bg-slate-100 text-slate-600"}`}>
            {t(`t360f.status.${kind}.${status}`, undefined, String(status))}
          </span>
        </div>
        {failure && <div className="text-xs text-rose-600 mt-1">{failure}</div>}
        <div className="text-xs text-slate-400">{t("t360.by")} {by} · {instantDay(at, lang) || "—"}</div>
      </div>
    </div>
  );
}

// Price-history event types by outcome: confirmed facts green, refusals and failures red, outcomes
// Stripe never confirmed amber, everything else neutral (D4 contract section 1.3).
const PRICE_EVENT_TONES = {
  set: "bg-emerald-100 text-emerald-700",
  set_confirmed: "bg-emerald-100 text-emerald-700",
  restored: "bg-emerald-100 text-emerald-700",
  restore_confirmed: "bg-emerald-100 text-emerald-700",
  set_refused: "bg-rose-100 text-rose-700",
  restore_refused: "bg-rose-100 text-rose-700",
  restore_failing: "bg-rose-100 text-rose-700",
  set_not_applied: "bg-rose-100 text-rose-700",
  restore_not_applied: "bg-rose-100 text-rose-700",
  set_unverified: "bg-amber-100 text-amber-700",
  restore_unverified: "bg-amber-100 text-amber-700",
};

function PriceEventRow({ event }) {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  return (
    <div className="flex items-start gap-3 text-sm bg-white rounded-lg p-3" data-testid={`price-${event.event_id}`}>
      <Wallet className="w-4 h-4 mt-0.5 text-emerald-600" />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Money value={event.price} />
          {event.cycle && <span className="text-xs text-slate-400">{t(`tl.cycle.${event.cycle}`, undefined, String(event.cycle))}</span>}
          <span className={`text-xs rounded-full px-2 py-0.5 ${PRICE_EVENT_TONES[event.type] || "bg-slate-100 text-slate-600"}`}>
            {t(`t360f.priceEvent.${event.type}`, undefined, String(event.type))}
          </span>
          <span className="text-xs text-slate-500">· {t(`t360f.priceTrigger.${event.trigger}`, undefined, String(event.trigger))}</span>
        </div>
        {event.reason && <div className="text-xs text-slate-500 break-words">{event.reason}</div>}
        {event.base && <div className="text-xs text-slate-500">{t("t360f.priceEvent.returnedTo", { price: moneyText(event.base) })}</div>}
        {event.error_code && <div className="text-xs text-rose-600 mt-1">{financeCodeText(event.error_code, t)}</div>}
        <div className="text-xs text-slate-400">{t("t360.by")} {event.actor} · {instantDay(event.at, lang) || "—"}</div>
      </div>
    </div>
  );
}

function HistoryList({ kind, list, page, setPage, rows, heading, noneKey, tone }) {
  const t = useT("superadmin");
  const { total, items } = list.data;
  return (
    <div className="mb-4 last:mb-0" data-testid={`t360-${kind}s-list`}>
      <div className={`text-xs uppercase tracking-wider mb-2 ${tone}`}>{heading}</div>
      {total === 0 ? (
        <p className="text-sm text-slate-400">{t(noneKey)}</p>
      ) : (
        <div className="space-y-2">{items.map(rows)}</div>
      )}
      {total > HISTORY_PAGE_SIZE && (
        <Pager
          page={page} pageSize={HISTORY_PAGE_SIZE} total={total} shown={items.length} onPage={setPage}
          infoText={(v) => t("sal.pagerInfo", v)} prevLabel={t("sal.pagerPrev")} nextLabel={t("sal.pagerNext")}
          testId={`t360-${kind}s-pager`} tone="slate"
        />
      )}
    </div>
  );
}

/**
 * A practice's rebates, refunds and price changes, newest first, three server-paged lists
 * (`GET …/billing/credits`, `GET …/billing/refunds`, `GET …/billing/price-history`). Always
 * rendered, in exactly one state: no access, loading, failed (with retry), empty, or the lists
 * (S7 contract section 4.2, D4 contract section 8.2).
 *
 * `refreshToken`: any change re-reads both lists from page 1 — the page bumps it after every
 * settled finance action (a refused attempt writes a row too) and after a custom price is removed.
 */
export default function FinancialHistory({ tenantId, refreshToken }) {
  const t = useT("superadmin");
  const tc = useT("common");
  const canRead = useSecondaryRead("tenantCredits");
  const [creditPage, setCreditPage] = useState(1);
  const [refundPage, setRefundPage] = useState(1);
  const [pricePage, setPricePage] = useState(1);
  const base = `/super-admin/tenants/${tenantId}/billing`;
  const credits = usePagedList({ path: `${base}/credits`, filters: {}, page: creditPage, setPage: setCreditPage, pageSize: HISTORY_PAGE_SIZE, enabled: canRead });
  const refunds = usePagedList({ path: `${base}/refunds`, filters: {}, page: refundPage, setPage: setRefundPage, pageSize: HISTORY_PAGE_SIZE, enabled: canRead });
  const prices = usePagedList({ path: `${base}/price-history`, filters: {}, page: pricePage, setPage: setPricePage, pageSize: HISTORY_PAGE_SIZE, enabled: canRead });

  const seenToken = useRef(refreshToken);
  const { reload: reloadCredits } = credits;
  const { reload: reloadRefunds } = refunds;
  const { reload: reloadPrices } = prices;
  useEffect(() => {
    if (seenToken.current === refreshToken) return;
    seenToken.current = refreshToken;
    setCreditPage(1);
    setRefundPage(1);
    setPricePage(1);
    reloadCredits();
    reloadRefunds();
    reloadPrices();
  }, [refreshToken, reloadCredits, reloadRefunds, reloadPrices]);

  let body;
  const failure = credits.loadError || refunds.loadError || prices.loadError;
  if (!canRead || (failure && isForbidden(failure))) {
    body = <NoAccessPanel testId="t360-history-forbidden" />;
  } else if (failure) {
    body = (
      <LoadFailure
        message={tc("errors.loadFailed")}
        onRetry={() => { credits.retry(); refunds.retry(); prices.retry(); }}
        retryLabel={t("sal.retry")} testId="t360-history-error" retryTestId="t360-history-error-retry"
      />
    );
  } else if (!credits.data || !refunds.data || !prices.data) {
    body = (
      <div data-testid="t360-history-loading" className="flex items-center gap-2 text-sm text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin text-amber-600" /> {t("t360f.history.loading")}
      </div>
    );
  } else if (credits.data.total === 0 && refunds.data.total === 0 && prices.data.total === 0) {
    body = <p className="text-sm text-slate-400" data-testid="t360-history-empty">{t("t360f.history.empty")}</p>;
  } else {
    body = (
      <>
        <HistoryList
          kind="credit" list={credits} page={creditPage} setPage={setCreditPage}
          heading={t("t360.rebatesCredits")} noneKey="t360f.history.noRebates" tone="text-amber-600"
          rows={(c) => (
            <HistoryRow key={c.credit_id} kind="credit" id={c.credit_id} icon={Percent} amount={c.amount} reason={c.reason}
              by={c.created_by} at={c.created_at} status={c.status} errorCode={c.error_code} />
          )}
        />
        <HistoryList
          kind="refund" list={refunds} page={refundPage} setPage={setRefundPage}
          heading={t("t360.refundsTitle")} noneKey="t360f.history.noRefunds" tone="text-rose-600"
          rows={(r) => (
            <HistoryRow key={r.refund_id} kind="refund" id={r.refund_id} icon={RefreshCcw} amount={r.amount} reason={r.reason}
              by={r.issued_by} at={r.issued_at} status={r.status} errorCode={r.error_code} />
          )}
        />
        <HistoryList
          kind="price" list={prices} page={pricePage} setPage={setPricePage}
          heading={t("t360f.history.prices")} noneKey="t360f.history.noPrices" tone="text-emerald-600"
          rows={(e) => <PriceEventRow key={e.event_id} event={e} />}
        />
      </>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="t360-history">
      <h3 className="font-sol font-semibold text-xl mb-4">{t("t360.financialHistory")}</h3>
      {body}
    </div>
  );
}
